/**
 * Read-boundary normalisation for saved-cart records.
 *
 * WHY THIS EXISTS (ATL-963 / QARASAY-KASSA-2B2, second route):
 * `NewSaleScreen`'s cart state is an array by construction — every writer goes through
 * `computeCartAddition` (utils/cartAddition.js) or an explicit `setCartItems([])`. There is
 * exactly ONE writer that puts externally-sourced data straight into that state:
 * `SavedCartsModal`'s Select button, `props.setCartItems(item.cartItems)`.
 *
 * The two read paths that fill `context.savedCarts` were asymmetric:
 *   - Supabase: `transformSavedCart` in the backend already coerces `items || []`, so an
 *     items-less row reads back as an empty array.
 *   - Firestore: the `onSnapshot` handler pushed `doc.data()` through untouched, so a
 *     document with no `cartItems` field (or a non-array one) restored `undefined` into the
 *     cart. `[...cartItems]` then threw during the next add/quantity change — the same crash
 *     2B2 was filed for, reached by a different route.
 *
 * The fix belongs HERE, at the boundary where the record enters the app, not as a
 * `cartItems ? [...cartItems] : []` guard at the throwing line: such a guard silently
 * substitutes an empty cart for a broken one and is exactly what kept 2B2 invisible.
 * Normalising here makes the invariant ("the cart state is always an array") TRUE rather
 * than merely assumed, and reports the malformed record instead of hiding it.
 *
 * A saved cart is a draft, not a book entry: a record whose lines are missing has nothing to
 * lose, and NewSaleScreen refuses to complete a sale from an empty cart, so restoring it as
 * empty cannot produce a wrong sale. What must not happen is that it crashes the till, or
 * that it disappears from the list so nobody can delete it.
 */

import * as Sentry from '@sentry/react-native'

/**
 * True when `cartItems` is a shape the cart state can hold.
 */
const hasArrayCartItems = (record) => Array.isArray(record?.cartItems)

/**
 * Coerce one saved-cart record so `cartItems` is always an array.
 * Returns the SAME object when it is already well-shaped, so React's referential checks and
 * the modal's memo comparator are unaffected for the normal case.
 *
 * @param {Object} record  raw saved-cart record from Firestore or the backend
 * @returns {Object} record with an array `cartItems`
 */
export const normalizeSavedCart = (record) => {
  if (!record || typeof record !== 'object') {
    return record
  }
  if (hasArrayCartItems(record)) {
    return record
  }
  return { ...record, cartItems: [] }
}

/**
 * Normalise a list of saved-cart records and report the malformed ones.
 *
 * @param {Array} records  raw records from one read path
 * @param {string} source  'firestore' | 'supabase' — which read path produced them
 * @returns {Array} records with an array `cartItems` on every entry
 */
export const normalizeSavedCarts = (records, source) => {
  if (!Array.isArray(records)) {
    // The read paths always build an array; anything else is a defect in the caller, and an
    // empty list is the only shape the modal can render. Report it rather than throwing in
    // a snapshot callback, where the throw would kill the listener and freeze the list.
    Sentry.captureMessage('savedCarts read path produced a non-array', {
      level: 'error',
      tags: { saved_cart_malformed: true, saved_cart_source: source },
      extra: { receivedType: typeof records },
    })
    return []
  }

  return records.map((record) => {
    const normalized = normalizeSavedCart(record)
    if (normalized !== record) {
      Sentry.captureMessage('savedCart has no cartItems array', {
        level: 'warning',
        tags: { saved_cart_malformed: true, saved_cart_source: source },
        extra: {
          savedCartId: record?.id || record?.firestoreId || null,
          cartItemsType: Array.isArray(record?.cartItems) ? 'array' : typeof record?.cartItems,
        },
      })
    }
    return normalized
  })
}
